import { Button } from '@foam/buoy/src/components/Button/Button';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import {
  FormControl,
  FormHelperText,
  Paragraph,
  TextLink,
  Textarea,
} from 'ui-components';
import { convertPxToRems } from '~/utils-ts';

import { MessageToolFormValues } from './MessageToolModal';
import { MESSAGE_MAX_CHARACTERS } from './constants';
import { useMessageTemplates } from './useMessageTemplates';
import { useMessageToolStore } from './useMessageToolStore';
import { trackMessageToolEvent } from './utils';

export const MessageStep = () => {
  const { selectedQuestion, setSelectedQuestion, creator, list, link } =
    useMessageToolStore();

  const { values, setFieldValue } = useFormikContext<MessageToolFormValues>();

  const messages = useMessageTemplates(creator);

  const displayRestoreMessageBtn = useMemo(() => {
    return (
      selectedQuestion !== null &&
      messages[selectedQuestion].message !== values.message
    );
  }, [selectedQuestion, messages, values.message]);

  const handleQuestionClick = useCallback(
    (questionIndex: number) => {
      setSelectedQuestion(questionIndex);
      setFieldValue('message', messages[questionIndex].message);
      if (!creator || !list || !link) return;

      trackMessageToolEvent({
        eventType: 'quick_question_clicked',
        creator,
        list,
        link,
        template: messages[questionIndex],
      });
    },
    [creator, list, link, messages, setSelectedQuestion, setFieldValue],
  );

  const handleResetToDefault = useCallback(() => {
    if (selectedQuestion === null) return;
    setFieldValue('message', messages[selectedQuestion].message);
  }, [messages, selectedQuestion, setFieldValue]);

  useEffect(() => {
    if (values.message.trim() === '') {
      setSelectedQuestion(null);
    }
  }, [values.message, setSelectedQuestion]);

  return (
    <div className="flex flex-col gap-6">
      <p>
        Send a message to {creator?.manager?.name}, {creator?.fullName}'s{' '}
        manager, using one of the quick questions below or write your own custom
        message.
      </p>

      <div className="flex flex-col gap-2">
        <p className="font-500">Quick questions</p>

        <div className="grid grid-cols-1 gap-2 tab:grid-cols-2">
          {messages.map((question, index) => (
            <Button
              key={index}
              iconStart={question.icon}
              variant="tertiary"
              onClick={() => handleQuestionClick(index)}
              className={`focus:bg-neutral-200 ${
                selectedQuestion === index ? 'bg-neutral-200' : 'bg-neutral-50'
              }`}
              type="button"
              tabIndex={-1}
            >
              {question.title}
            </Button>
          ))}
        </div>
      </div>

      <FormControl>
        <div className="mb-2 flex items-center justify-between">
          <Paragraph as="span" fontWeight="medium">
            Your message
          </Paragraph>
          <TextLink
            onClick={handleResetToDefault}
            as="span"
            variant="primary"
            sx={{
              visibility: displayRestoreMessageBtn ? 'visible' : 'hidden',
              opacity: displayRestoreMessageBtn ? 1 : 0,
              fontWeight: 'medium',
              _hover: {
                textDecoration: 'none',
              },
            }}
          >
            Reset to default
          </TextLink>
        </div>
        <Textarea
          name="message"
          aria-label="message"
          value={values.message}
          height={convertPxToRems(218)}
          onClear={() => setSelectedQuestion(null)}
          overflowY="auto"
          maxLength={MESSAGE_MAX_CHARACTERS}
          tabIndex={-1}
        />
        <FormHelperText
          fontSize="md"
          color="primary.300"
          sx={{ marginTop: 2, marginLeft: 3 }}
        >
          {values.message.length}/{MESSAGE_MAX_CHARACTERS} characters
        </FormHelperText>
      </FormControl>
    </div>
  );
};
